(() => {
  const STORAGE_KEY = 'marline_accessibility_v1';
  const TEXT_SCALES = [1, 1.1, 1.2, 1.3, 1.4];
  const SUPPORTED_LANGUAGES = ['he', 'en', 'ar'];

  const DEFAULT_STATE = Object.freeze({
    textScale: 1,
    readableFont: false,
    highContrast: false,
    yellowBlack: false,
    grayscale: false,
    highlightLinks: false,
    highlightHeadings: false,
    textSpacing: false,
    reduceMotion: false,
    focusMode: false,
    bigCursor: false,
    language: 'he'
  });

  const TRANSLATIONS = {
    he: {
      title: 'התאמות תצוגה וניווט',
      subtitle: 'התאימו את האתר לצרכים שלכם',
      textSize: 'גודל טקסט',
      readableFont: 'גופן קריא',
      highContrast: 'ניגודיות גבוהה',
      yellowBlack: 'שחור / צהוב',
      grayscale: 'גווני אפור',
      highlightLinks: 'הדגשת קישורים',
      highlightHeadings: 'הדגשת כותרות',
      textSpacing: 'ריווח טקסט',
      reduceMotion: 'עצירת אנימציות',
      focusMode: 'מיקוד מקלדת',
      bigCursor: 'סמן גדול',
      reset: 'איפוס כל ההתאמות',
      saved: 'ההתאמות נשמרות במכשיר הזה',
      escClose: 'לסגירה',
      launcherLabel: 'פתיחת כלי התאמה',
      closeLabel: 'סגירת כלי התאמה',
      languageGroupLabel: 'בחירת שפה',
      textSizeGroupLabel: 'שינוי גודל הטקסט',
      textDecreaseLabel: 'הקטנת טקסט',
      textIncreaseLabel: 'הגדלת טקסט',
      enabled: 'הופעל',
      disabled: 'בוטל',
      resetAnnouncement: 'כל ההתאמות אופסו',
      textScaleAnnouncement: (value) => `גודל הטקסט הוגדר ל-${value} אחוז`
    },
    en: {
      title: 'Display & navigation',
      subtitle: 'Adjust the site to suit your needs',
      textSize: 'Text size',
      readableFont: 'Readable font',
      highContrast: 'High contrast',
      yellowBlack: 'Black / yellow',
      grayscale: 'Grayscale',
      highlightLinks: 'Highlight links',
      highlightHeadings: 'Highlight headings',
      textSpacing: 'Text spacing',
      reduceMotion: 'Stop animations',
      focusMode: 'Keyboard focus',
      bigCursor: 'Large cursor',
      reset: 'Reset all adjustments',
      saved: 'Your settings are saved on this device',
      escClose: 'to close',
      launcherLabel: 'Open display and navigation controls',
      closeLabel: 'Close display and navigation controls',
      languageGroupLabel: 'Choose language',
      textSizeGroupLabel: 'Change text size',
      textDecreaseLabel: 'Decrease text size',
      textIncreaseLabel: 'Increase text size',
      enabled: 'enabled',
      disabled: 'disabled',
      resetAnnouncement: 'All adjustments have been reset',
      textScaleAnnouncement: (value) => `Text size set to ${value} percent`
    },
    ar: {
      title: 'العرض والتنقل',
      subtitle: 'خصّص الموقع بما يناسب احتياجاتك',
      textSize: 'حجم النص',
      readableFont: 'خط سهل القراءة',
      highContrast: 'تباين عالٍ',
      yellowBlack: 'أسود / أصفر',
      grayscale: 'تدرج رمادي',
      highlightLinks: 'تمييز الروابط',
      highlightHeadings: 'تمييز العناوين',
      textSpacing: 'تباعد النص',
      reduceMotion: 'إيقاف الحركات',
      focusMode: 'تركيز لوحة المفاتيح',
      bigCursor: 'مؤشر كبير',
      reset: 'إعادة ضبط جميع الإعدادات',
      saved: 'تُحفظ الإعدادات على هذا الجهاز',
      escClose: 'للإغلاق',
      launcherLabel: 'فتح أدوات العرض والتنقل',
      closeLabel: 'إغلاق أدوات العرض والتنقل',
      languageGroupLabel: 'اختيار اللغة',
      textSizeGroupLabel: 'تغيير حجم النص',
      textDecreaseLabel: 'تصغير حجم النص',
      textIncreaseLabel: 'تكبير حجم النص',
      enabled: 'مفعّل',
      disabled: 'متوقف',
      resetAnnouncement: 'تمت إعادة ضبط جميع الإعدادات',
      textScaleAnnouncement: (value) => `تم ضبط حجم النص على ${value} بالمئة`
    }
  };

  const BODY_CLASSES = {
    readableFont: 'marl-a11y-readable-font',
    highContrast: 'marl-a11y-high-contrast',
    yellowBlack: 'marl-a11y-yellow-black',
    grayscale: 'marl-a11y-grayscale',
    highlightLinks: 'marl-a11y-highlight-links',
    highlightHeadings: 'marl-a11y-highlight-headings',
    textSpacing: 'marl-a11y-text-spacing',
    reduceMotion: 'marl-a11y-reduce-motion',
    focusMode: 'marl-a11y-focus-mode',
    bigCursor: 'marl-a11y-big-cursor'
  };

  const VISUAL_MODES = ['highContrast', 'yellowBlack', 'grayscale'];

  const safeParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  };

  const normalizeLanguage = (language) => {
    const code = String(language || '').toLowerCase().slice(0, 2);
    return SUPPORTED_LANGUAGES.includes(code) ? code : 'he';
  };

  const getSavedState = (defaultLanguage) => {
    let raw = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      raw = null;
    }

    const stored = safeParse(raw);
    const state = { ...DEFAULT_STATE, language: defaultLanguage };
    if (!stored || typeof stored !== 'object') return state;

    Object.keys(BODY_CLASSES).forEach((key) => {
      state[key] = stored[key] === true;
    });

    const requestedScale = Number(stored.textScale);
    state.textScale = TEXT_SCALES.includes(requestedScale) ? requestedScale : 1;
    state.language = SUPPORTED_LANGUAGES.includes(stored.language) ? stored.language : defaultLanguage;
    return state;
  };

  const init = () => {
    const root = document.querySelector('[data-marl-a11y-root]');
    if (!root || root.dataset.marlA11yReady === 'true') return;
    root.dataset.marlA11yReady = 'true';

    const body = document.body;
    const html = document.documentElement;
    const launcher = root.querySelector('[data-marl-a11y-launcher]');
    const panel = root.querySelector('[data-marl-a11y-panel]');
    const closeButton = root.querySelector('[data-marl-a11y-close]');
    const resetButton = root.querySelector('[data-marl-a11y-reset]');
    const scaleValue = root.querySelector('[data-marl-a11y-scale-value]');
    const status = root.querySelector('[data-marl-a11y-status]');
    const toggleButtons = Array.from(root.querySelectorAll('[data-marl-a11y-toggle]'));
    const languageButtons = Array.from(root.querySelectorAll('[data-marl-a11y-language-option]'));
    const decreaseButton = root.querySelector('[data-marl-a11y-action="text-decrease"]');
    const increaseButton = root.querySelector('[data-marl-a11y-action="text-increase"]');

    if (!body || !launcher || !panel || !closeButton) return;

    const pageLanguage = normalizeLanguage(root.dataset.marlA11yLanguage || html.lang);
    const baseRootFontPx = parseFloat(window.getComputedStyle(html).fontSize) || 10;
    let state = getSavedState(pageLanguage);
    let hideTimer = null;
    let lastFocusedElement = null;

    const t = (key) => TRANSLATIONS[state.language]?.[key] ?? TRANSLATIONS.he[key] ?? key;

    const saveState = () => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (_) {
        // localStorage may be unavailable in strict privacy modes; controls still work for the session.
      }
    };

    const announce = (message) => {
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = message;
      }, 20);
    };

    const applyLanguage = () => {
      const isLtr = state.language === 'en';
      root.setAttribute('dir', isLtr ? 'ltr' : 'rtl');
      root.setAttribute('lang', state.language);

      root.querySelectorAll('[data-marl-a11y-i18n]').forEach((element) => {
        const key = element.dataset.marlA11yI18n;
        const value = t(key);
        if (typeof value === 'string') element.textContent = value;
      });

      root.querySelectorAll('[data-marl-a11y-aria]').forEach((element) => {
        const key = element.dataset.marlA11yAria;
        const value = t(key);
        if (typeof value === 'string') element.setAttribute('aria-label', value);
      });

      languageButtons.forEach((button) => {
        const isActive = button.dataset.marlA11yLanguageOption === state.language;
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const applyTextScale = () => {
      if (state.textScale === 1) {
        html.style.removeProperty('font-size');
      } else {
        html.style.fontSize = `${(baseRootFontPx * state.textScale).toFixed(2)}px`;
      }

      if (scaleValue) scaleValue.textContent = `${Math.round(state.textScale * 100)}%`;
      if (decreaseButton) decreaseButton.disabled = state.textScale <= TEXT_SCALES[0];
      if (increaseButton) increaseButton.disabled = state.textScale >= TEXT_SCALES[TEXT_SCALES.length - 1];
    };

    const applyState = ({ persist = true } = {}) => {
      Object.entries(BODY_CLASSES).forEach(([key, className]) => {
        body.classList.toggle(className, state[key] === true);
      });

      applyLanguage();
      applyTextScale();

      toggleButtons.forEach((button) => {
        const key = button.dataset.marlA11yToggle;
        button.setAttribute('aria-pressed', state[key] ? 'true' : 'false');
      });

      if (persist) saveState();
    };

    const setVisualMode = (key) => {
      const willEnable = !state[key];
      VISUAL_MODES.forEach((mode) => {
        state[mode] = false;
      });
      state[key] = willEnable;
    };

    const toggleSetting = (key) => {
      if (!(key in BODY_CLASSES)) return;

      if (VISUAL_MODES.includes(key)) {
        setVisualMode(key);
      } else {
        state[key] = !state[key];
      }

      applyState();
      announce(`${t(key)} ${state[key] ? t('enabled') : t('disabled')}`);
    };

    const changeTextScale = (direction) => {
      const currentIndex = TEXT_SCALES.indexOf(state.textScale);
      const safeIndex = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex = Math.min(TEXT_SCALES.length - 1, Math.max(0, safeIndex + direction));
      const nextScale = TEXT_SCALES[nextIndex];
      if (nextScale === state.textScale) return;

      state.textScale = nextScale;
      applyState();
      const announcement = t('textScaleAnnouncement');
      if (typeof announcement === 'function') {
        announce(announcement(Math.round(nextScale * 100)));
      }
    };

    const setLanguage = (language) => {
      const nextLanguage = normalizeLanguage(language);
      if (nextLanguage === state.language) return;
      state.language = nextLanguage;
      applyState();
    };

    const openPanel = () => {
      if (hideTimer) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (!panel.hidden) return;

      lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : launcher;
      panel.hidden = false;
      launcher.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        root.classList.add('is-open');
        closeButton.focus({ preventScroll: true });
      });
    };

    const closePanel = ({ restoreFocus = true } = {}) => {
      if (panel.hidden) return;
      root.classList.remove('is-open');
      launcher.setAttribute('aria-expanded', 'false');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = reduceMotion ? 0 : 180;
      hideTimer = window.setTimeout(() => {
        panel.hidden = true;
        hideTimer = null;
      }, delay);

      if (restoreFocus && lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
    };

    const resetAll = () => {
      const currentLanguage = state.language;
      state = { ...DEFAULT_STATE, language: currentLanguage };
      applyState();
      announce(t('resetAnnouncement'));
    };

    launcher.addEventListener('click', () => {
      if (panel.hidden) openPanel();
      else closePanel();
    });

    closeButton.addEventListener('click', () => closePanel());

    toggleButtons.forEach((button) => {
      button.addEventListener('click', () => toggleSetting(button.dataset.marlA11yToggle));
    });

    languageButtons.forEach((button) => {
      button.addEventListener('click', () => setLanguage(button.dataset.marlA11yLanguageOption));
    });

    decreaseButton?.addEventListener('click', () => changeTextScale(-1));
    increaseButton?.addEventListener('click', () => changeTextScale(1));
    resetButton?.addEventListener('click', resetAll);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        closePanel();
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (panel.hidden) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!root.contains(target)) closePanel({ restoreFocus: false });
    });

    applyState({ persist: false });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
